import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";

/**
 * Global Notes Market status. While `live` is false, the public /notes page and
 * the in-app Notes Market surfaces render a locked "Coming Soon" state with a
 * notify-me signup, and the notes edge functions reject buy/list/subscribe.
 */
export function useNotesMarketStatus() {
  const { data, isLoading } = useQuery({
    queryKey: ["platform_settings", "notes_market"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_public_platform_settings");
      if (error) throw error;
      return data as any;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const live = (data?.notes_market_live as boolean) ?? false;
  return { live, locked: !live, loading: isLoading, settingsId: data?.id as string | undefined };
}

export function useSetNotesMarketLive() {
  const qc = useQueryClient();
  const { settingsId } = usePlatformSettings();

  return useMutation({
    mutationFn: async (live: boolean) => {
      if (!settingsId) throw new Error("Platform settings not initialised");
      const { error } = await supabase
        .from("platform_settings" as any)
        .update({ notes_market_live: live } as any)
        .eq("id", settingsId);
      if (error) throw error;
      return live;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["platform_settings"] });
    },
  });
}
